import type { StandardResponse } from '@/types/common';
import type {
  CreateVariablePayload,
  CreateCohortVariablePayload,
  MyVariable,
  UpdateVariablePayload,
  UserVariableUpdatePayload,
  Variable,
} from '@/types/variables';
import api from './utils/axios';
import generateCRUDOps from './utils/generateCrud';

const pathPrefix = '/agentos/';

export type VariableListQueryParams = {
  filters?: Record<string, string | number | boolean | undefined>;
};

export type VariableEntriesQueryParams = Record<string, string>;

const kbResourceOps = generateCRUDOps(pathPrefix, 'kbresources');

const VariableService = {
  ...kbResourceOps,

  getVariables: async (
    params?: VariableListQueryParams
  ): Promise<StandardResponse<Variable>> => {
    const { data } = await api.get(`${pathPrefix}variables/`, {
      params: params?.filters,
    });
    return data;
  },

  getVariablesById: async (id: number): Promise<Variable> => {
    const { data } = await api.get(`${pathPrefix}variables/${id}/`);
    return data;
  },

  createVariables: async (
    payload: CreateVariablePayload
  ): Promise<StandardResponse<Variable>> => {
    const { data } = await api.post(`${pathPrefix}variables/`, payload);
    return data;
  },

  createCohortVariables: async (
    payload: CreateCohortVariablePayload
  ): Promise<Variable[]> => {
    const { data } = await api.post(`${pathPrefix}variables/`, payload);
    return Array.isArray(data) ? data : data.results ?? [];
  },

  updateVariables: async (
    id: number,
    payload: UpdateVariablePayload
  ): Promise<Variable> => {
    const { data } = await api.patch(`${pathPrefix}variables/${id}/`, payload);
    return data;
  },

  deleteVariables: async (id: number): Promise<void> => {
    await api.delete(`${pathPrefix}variables/${id}/`);
  },

  getMyVariables: async (
    params?: VariableListQueryParams,
    options?: { tenantId?: number; suppressErrorToast?: boolean }
  ): Promise<StandardResponse<MyVariable>> => {
    const { data } = await api.get(`${pathPrefix}my-variables/`, {
      params: params?.filters,
      suppressErrorToast: options?.suppressErrorToast,
      // Login runs on /login or /activate, where the URL carries no tenant.
      headers: options?.tenantId
        ? { 'x-tenant-id': options.tenantId.toString() }
        : undefined,
    });
    return data;
  },

  getMyVariablesById: async (id: number): Promise<MyVariable> => {
    const { data } = await api.get(`${pathPrefix}my-variables/${id}/`);
    return data;
  },

  updateUserVariable: async (
    payload: UserVariableUpdatePayload,
    options?: { suppressErrorToast?: boolean }
  ): Promise<MyVariable> => {
    const { data } = await api.patch(
      `${pathPrefix}my-variables/update-values/`,
      payload,
      { suppressErrorToast: options?.suppressErrorToast }
    );
    return data;
  },

  triggerExtraction: async (
    payload: Record<string, number | undefined>
  ): Promise<StandardResponse<MyVariable>> => {
    const { data } = await api.post(
      `${pathPrefix}my-variables/trigger-extraction/`,
      payload
    );
    return data;
  },

  getAllVariablesWithValues: async (
    params: VariableEntriesQueryParams
  ): Promise<StandardResponse<Variable>> => {
    const { data } = await api.get(`${pathPrefix}variables/entries/`, {
      params,
    });
    return data;
  },
};

export default VariableService;
